import React, { useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { Upload, X, Loader, Plus } from 'lucide-react';
import { v4 as uuidv4 } from 'uuid';
import { supabase } from '../lib/db';
import toast from 'react-hot-toast';
import { useLanguage } from '../contexts/LanguageContext';
import { isVideoUrl, ACCEPTED_MEDIA_TYPES, MAX_VIDEO_SIZE } from '../utils/mediaUtils';

interface ImageUploadProps {
  onUpload: (url: string) => void;
  currentImage?: string;
  quality?: number; // Optional quality parameter (0-1)
  compact?: boolean; // Small square upload button
}

export const ImageUpload: React.FC<ImageUploadProps> = ({ 
  onUpload, 
  currentImage,
  quality = 0.7, // Default compression quality (70%)
  compact = false,
}) => {
  const { t } = useLanguage();
  const [isCompressing, setIsCompressing] = useState(false);

  // Function to compress the image
  const compressImage = useCallback(async (file: File): Promise<File> => {
    return new Promise((resolve, reject) => {
      setIsCompressing(true);
      
      const reader = new FileReader();
      reader.readAsDataURL(file);
      
      reader.onload = (event) => {
        const img = new Image();
        img.src = event.target?.result as string;
        
        img.onload = () => {
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          
          // Maintain original dimensions
          canvas.width = img.width;
          canvas.height = img.height;
          
          // Draw image on canvas
          ctx?.drawImage(img, 0, 0, canvas.width, canvas.height);
          
          // Convert to blob with compression
          canvas.toBlob(
            (blob) => {
              if (!blob) {
                setIsCompressing(false);
                reject(new Error('Failed to compress image'));
                return;
              }
              
              // Create new file from blob
              const compressedFile = new File([blob], file.name, {
                type: 'image/jpeg', // Convert to JPEG for better compression
                lastModified: Date.now(),
              });
              
              setIsCompressing(false);
              resolve(compressedFile);
            },
            'image/jpeg',
            quality // Compression quality (0-1)
          );
        };
        
        img.onerror = () => {
          setIsCompressing(false);
          reject(new Error('Failed to load image'));
        };
      };
      
      reader.onerror = () => {
        setIsCompressing(false);
        reject(new Error('Failed to read file'));
      };
    });
  }, [quality]);

  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    const file = acceptedFiles[0];
    if (!file) return;

    try {
      const isVideo = file.type.startsWith('video/');
      let fileToUpload: File;

      if (isVideo) {
        // Upload video directly without compression
        fileToUpload = file;
      } else {
        // Compress the image before uploading
        fileToUpload = await compressImage(file);
        console.log('Original size:', (file.size / 1024 / 1024).toFixed(2) + 'MB');
        console.log('Compressed size:', (fileToUpload.size / 1024 / 1024).toFixed(2) + 'MB');
      }
      
      const fileExt = file.name.split('.').pop();
      const fileName = `${uuidv4()}.${fileExt}`;
      const filePath = `images/${fileName}`;

      // Upload the file
      const { error: uploadError } = await supabase.storage
        .from('images')
        .upload(filePath, fileToUpload, {
          cacheControl: '3600',
          upsert: true
        });

      if (uploadError) {
        throw uploadError;
      }

      // Get the public URL
      const { data: { publicUrl } } = supabase.storage
        .from('images')
        .getPublicUrl(filePath);

      onUpload(publicUrl);
      toast.success(t('imageUploadSuccess'));
    } catch (error: any) {
      console.error(t('imageUploadError'), error);
      toast.error(t('imageUploadError'));
    }
  }, [onUpload, t, compressImage]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: ACCEPTED_MEDIA_TYPES,
    maxFiles: 1,
    multiple: false,
    maxSize: MAX_VIDEO_SIZE // 50MB to accommodate videos
  });

  const removeImage = async () => {
    if (!currentImage) return;

    try {
      const fileName = currentImage.split('/').pop();
      if (!fileName) return;

      const filePath = `images/${fileName}`;

      const { error } = await supabase.storage
        .from('images')
        .remove([filePath]);

      if (error) {
        throw error;
      }

      onUpload('');
      toast.success(t('imageRemoveSuccess'));
    } catch (error: any) {
      console.error(t('imageRemoveError'), error);
      toast.error(t('imageRemoveError'));
    }
  };

  return (
    <div className={compact ? '' : 'space-y-4'}>
      {currentImage && !compact ? (
        <div className="relative">
          {isVideoUrl(currentImage) ? (
            <video
              src={currentImage}
              className="w-full h-48 object-cover rounded-lg"
              muted
              playsInline
              preload="metadata"
              controls
            />
          ) : (
            <img
              src={currentImage}
              alt={t('imageAlt')}
              className="w-full h-48 object-cover rounded-lg"
            />
          )}
          <button
            onClick={removeImage}
            className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
            type="button"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      ) : (
        compact ? (
          <div
            {...getRootProps()}
            className={`w-full h-full border-2 border-dashed rounded-lg flex items-center justify-center cursor-pointer transition-colors
              ${isDragActive ? 'border-primary-500 bg-primary-50' : 'border-gray-300 hover:border-primary-500 hover:bg-gray-50'}`}
          >
            <input {...getInputProps()} className="hidden" />
            {isCompressing ? (
              <Loader className="w-4 h-4 text-primary-500 animate-spin" />
            ) : (
              <Plus className="w-5 h-5 text-gray-400" />
            )}
          </div>
        ) : (
        <div
          {...getRootProps()}
          className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors w-full max-w-[200px] mx-auto flex items-center justify-center
            ${isDragActive ? 'border-primary-500 bg-primary-50' : 'border-gray-300 hover:border-primary-500'}`}
        >
          <input {...getInputProps()} className="hidden" />
          <div className="flex flex-col items-center justify-center">
            {isCompressing ? (
              <>
                <Loader className="w-8 h-8 text-primary-500 animate-spin" />
                <p className="text-sm text-gray-600 mt-2">{t('compressing')}</p>
              </>
            ) : (
              <>
                <Upload className="w-8 h-8 text-gray-400" />
                <p className="text-sm text-gray-600 mt-2">
                  {isDragActive ? t('dropImage') : t('dragOrClick')}
                </p>
              </>
            )}
          </div>
        </div>
        )
      )}
    </div>
  );
};
